import React, { useState, useEffect, useCallback } from "react";
import {
  Bot,
  Plus,
  Trash2,
  Edit3,
  Save,
  X,
  ChevronDown,
  ChevronUp,
  Search,
  Upload,
  HelpCircle,
  MessageSquare,
  Settings,
  Sparkles,
  Check,
  Loader2,
  Power,
  Users,
  Zap,
  AlertCircle,
  BookOpen,
  Target,
  Play,
  Pause,
  GripVertical,
} from "lucide-react";
import {
  fetchClaw9Accounts,
  fetchClaw9FAQs,
  publishClaw9FAQs,
  updateClaw9FAQ,
  createClaw9FAQ,
  deleteClaw9FAQ,
  bulkCreateClaw9FAQs,
  generateClaw9FAQVariations,
  fetchBotAgents,
  toggleBotAgent,
  publishFAQsWithKnowledge,
  fetchTargetScripts,
  createTargetScript,
  updateTargetScript,
  deleteTargetScript,
  toggleTargetScript,
} from "../api/claw9";
import Claw9KnowledgeBase from "./Claw9KnowledgeBase";

// Tabs configuration
const AGENT_TABS = [
  { id: "agents", label: "Agents", icon: Users, description: "Manage bot agents" },
  { id: "faqs", label: "FAQs", icon: HelpCircle, description: "Knowledge base Q&A" },
  { id: "scripts", label: "Scripts", icon: Target, description: "Sales flow scripts" },
  { id: "knowledge", label: "AI Knowledge", icon: BookOpen, description: "AI-generated knowledge base" },
  { id: "prompts", label: "Prompts", icon: MessageSquare, description: "Custom prompts", disabled: true },
  { id: "settings", label: "Settings", icon: Settings, description: "Bot configuration", disabled: true },
];

// Script goal options
const SCRIPT_GOALS = [
  { value: "order", label: "Đặt hàng", labelEn: "Order" },
  { value: "appointment", label: "Đặt lịch hẹn", labelEn: "Appointment" },
  { value: "consultation", label: "Tư vấn", labelEn: "Consultation" },
  { value: "info", label: "Cung cấp thông tin", labelEn: "Information" },
  { value: "custom", label: "Tùy chỉnh", labelEn: "Custom" },
];

// Collect field options
const COLLECT_FIELDS = [
  { value: "", label: "Không thu thập", labelEn: "None" },
  { value: "product", label: "Sản phẩm", labelEn: "Product" },
  { value: "quantity", label: "Số lượng", labelEn: "Quantity" },
  { value: "address", label: "Địa chỉ", labelEn: "Address" },
  { value: "phone", label: "Số điện thoại", labelEn: "Phone" },
  { value: "name", label: "Tên", labelEn: "Name" },
  { value: "email", label: "Email", labelEn: "Email" },
  { value: "date", label: "Ngày", labelEn: "Date" },
  { value: "time", label: "Giờ", labelEn: "Time" },
  { value: "confirmation", label: "Xác nhận", labelEn: "Confirmation" },
];

// Validation options
const VALIDATION_TYPES = [
  { value: "", label: "Không", labelEn: "None" },
  { value: "non_empty", label: "Bắt buộc", labelEn: "Required" },
  { value: "positive_integer", label: "Số dương", labelEn: "Positive number" },
  { value: "vietnam_phone", label: "SĐT Việt Nam", labelEn: "Vietnam phone" },
  { value: "yes_no", label: "Có/Không", labelEn: "Yes/No" },
];

// Goal icons
const GOAL_ICONS = {
  order: "💰",
  appointment: "📅",
  consultation: "💬",
  info: "ℹ️",
  custom: "⚙️",
};

// Script Templates
const SCRIPT_TEMPLATES = [
  {
    id: "order-basic",
    name: "Chốt Đơn Cơ Bản",
    description: "Quy trình đặt hàng đơn giản cho sản phẩm",
    icon: "💰",
    goal: "order",
    trigger_phrases: ["giá bao nhiêu", "bao nhiêu tiền", "muốn mua", "đặt hàng", "order", "mua ngay"],
    steps: [
      { step_id: 1, name: "Xác nhận sản phẩm", message: "Dạ anh/chị đang quan tâm sản phẩm nào ạ? Em có thể tư vấn chi tiết cho anh/chị ạ 😊", collect_field: "product", validation: "non_empty" },
      { step_id: 2, name: "Số lượng", message: "Dạ anh/chị muốn đặt bao nhiêu {product} ạ?", collect_field: "quantity", validation: "positive_integer" },
      { step_id: 3, name: "Địa chỉ giao hàng", message: "Anh/chị cho em xin địa chỉ ship hàng ạ?", collect_field: "address", validation: "non_empty" },
      { step_id: 4, name: "Số điện thoại", message: "Số điện thoại nhận hàng của anh/chị ạ?", collect_field: "phone", validation: "vietnam_phone" },
      { step_id: 5, name: "Xác nhận đơn", message: "Xác nhận đơn hàng:\n📦 {quantity}x {product}\n📍 Ship: {address}\n📞 SĐT: {phone}\n\nAnh/chị xác nhận đặt hàng không ạ?", collect_field: "confirmation", validation: "yes_no" },
    ],
    success_message: "Đặt hàng thành công! 🎉 Em sẽ gọi xác nhận trong 30 phút ạ. Cảm ơn anh/chị đã tin tưởng shop!",
    cancel_message: "Dạ không sao ạ! Khi nào anh/chị cần cứ nhắn em nhé 😊",
  },
  {
    id: "appointment-consultation",
    name: "Đặt Lịch Tư Vấn",
    description: "Đặt lịch hẹn tư vấn trực tiếp hoặc online",
    icon: "📅",
    goal: "appointment",
    trigger_phrases: ["đặt lịch", "hẹn gặp", "tư vấn trực tiếp", "book lịch", "muốn gặp", "lịch hẹn"],
    steps: [
      { step_id: 1, name: "Tên khách hàng", message: "Dạ để em đặt lịch hẹn, anh/chị cho em xin tên ạ?", collect_field: "name", validation: "non_empty" },
      { step_id: 2, name: "Nội dung tư vấn", message: "Anh/chị {name} muốn được tư vấn về vấn đề gì ạ?", collect_field: "topic", validation: "non_empty" },
      { step_id: 3, name: "Ngày hẹn", message: "Anh/chị muốn hẹn ngày nào ạ? (VD: 25/12 hoặc thứ 2 tuần sau)", collect_field: "date", validation: "non_empty" },
      { step_id: 4, name: "Giờ hẹn", message: "Anh/chị muốn hẹn lúc mấy giờ ạ?", collect_field: "time", validation: "non_empty" },
      { step_id: 5, name: "Số điện thoại", message: "Số điện thoại để em xác nhận lịch ạ?", collect_field: "phone", validation: "vietnam_phone" },
      { step_id: 6, name: "Xác nhận", message: "Xác nhận lịch hẹn:\n👤 {name}\n📋 Tư vấn: {topic}\n📅 Ngày: {date}\n⏰ Giờ: {time}\n📞 SĐT: {phone}\n\nAnh/chị xác nhận lịch hẹn này không ạ?", collect_field: "confirmation", validation: "yes_no" },
    ],
    success_message: "Đặt lịch thành công! 📅 Em sẽ gọi nhắc lịch trước 1 ngày ạ. Hẹn gặp anh/chị {name}!",
    cancel_message: "Dạ không sao ạ! Khi nào anh/chị rảnh cứ nhắn em đặt lịch nhé 😊",
  },
  {
    id: "service-booking",
    name: "Đặt Dịch Vụ Spa/Salon",
    description: "Đặt lịch làm đẹp, spa, salon",
    icon: "💅",
    goal: "appointment",
    trigger_phrases: ["đặt lịch làm", "book spa", "làm nail", "làm tóc", "chăm sóc da", "massage"],
    steps: [
      { step_id: 1, name: "Dịch vụ", message: "Chào anh/chị! 💅 Anh/chị muốn sử dụng dịch vụ gì ạ?\n\n1️⃣ Chăm sóc da mặt\n2️⃣ Massage body\n3️⃣ Làm nail\n4️⃣ Làm tóc\n5️⃣ Khác", collect_field: "service", validation: "non_empty" },
      { step_id: 2, name: "Ngày", message: "Anh/chị muốn đến vào ngày nào ạ?", collect_field: "date", validation: "non_empty" },
      { step_id: 3, name: "Giờ", message: "Anh/chị thích khung giờ nào ạ?\n🌅 Sáng (9h-12h)\n☀️ Chiều (14h-17h)\n🌙 Tối (18h-21h)", collect_field: "time", validation: "non_empty" },
      { step_id: 4, name: "Tên", message: "Tên của anh/chị để em đặt lịch ạ?", collect_field: "name", validation: "non_empty" },
      { step_id: 5, name: "SĐT", message: "Số điện thoại liên hệ ạ?", collect_field: "phone", validation: "vietnam_phone" },
      { step_id: 6, name: "Xác nhận", message: "✨ Xác nhận lịch hẹn:\n💅 Dịch vụ: {service}\n📅 Ngày: {date}\n⏰ Giờ: {time}\n👤 Tên: {name}\n📞 SĐT: {phone}\n\nAnh/chị xác nhận không ạ?", collect_field: "confirmation", validation: "yes_no" },
    ],
    success_message: "Đặt lịch thành công! ✨ Hẹn gặp anh/chị {name} vào {date} nhé! Em sẽ nhắn nhở trước 1 ngày ạ 💕",
    cancel_message: "Dạ không sao ạ! Khi nào anh/chị rảnh cứ book lịch nhé, em luôn sẵn sàng phục vụ 💅",
  },
  {
    id: "quote-request",
    name: "Yêu Cầu Báo Giá",
    description: "Thu thập thông tin để gửi báo giá chi tiết",
    icon: "📋",
    goal: "consultation",
    trigger_phrases: ["báo giá", "giá cả", "bảng giá", "quote", "chi phí", "phí dịch vụ"],
    steps: [
      { step_id: 1, name: "Sản phẩm/Dịch vụ", message: "Dạ anh/chị cần báo giá cho sản phẩm/dịch vụ nào ạ?", collect_field: "product", validation: "non_empty" },
      { step_id: 2, name: "Số lượng/Quy mô", message: "Anh/chị cần số lượng bao nhiêu hoặc quy mô như thế nào ạ?", collect_field: "quantity", validation: "non_empty" },
      { step_id: 3, name: "Yêu cầu đặc biệt", message: "Anh/chị có yêu cầu đặc biệt nào không ạ? (nhập 'không' nếu không có)", collect_field: "requirements", validation: null },
      { step_id: 4, name: "Email", message: "Email để em gửi báo giá chi tiết ạ?", collect_field: "email", validation: "non_empty" },
      { step_id: 5, name: "SĐT", message: "Số điện thoại để em liên hệ tư vấn thêm ạ?", collect_field: "phone", validation: "vietnam_phone" },
    ],
    success_message: "Cảm ơn anh/chị! 📧 Em sẽ gửi báo giá chi tiết qua email trong vòng 2 giờ ạ. Nếu cần gấp, anh/chị cứ gọi hotline nhé!",
    cancel_message: "Dạ không sao ạ! Khi nào anh/chị cần báo giá cứ nhắn em nhé 😊",
  },
  {
    id: "food-order",
    name: "Đặt Đồ Ăn/Thức Uống",
    description: "Quy trình đặt món cho nhà hàng, quán cafe",
    icon: "🍔",
    goal: "order",
    trigger_phrases: ["đặt món", "order đồ ăn", "giao hàng", "đặt ship", "menu", "thực đơn"],
    steps: [
      { step_id: 1, name: "Món đặt", message: "Chào anh/chị! 🍔 Anh/chị muốn đặt món gì ạ? (Có thể đặt nhiều món, cách nhau bằng dấu phẩy)", collect_field: "items", validation: "non_empty" },
      { step_id: 2, name: "Số lượng", message: "Mỗi món anh/chị lấy bao nhiêu phần ạ?", collect_field: "quantity", validation: "non_empty" },
      { step_id: 3, name: "Ghi chú", message: "Anh/chị có ghi chú gì không ạ? (ít đá, không hành, cay nhiều...)", collect_field: "notes", validation: null },
      { step_id: 4, name: "Địa chỉ", message: "Địa chỉ giao hàng của anh/chị ạ?", collect_field: "address", validation: "non_empty" },
      { step_id: 5, name: "SĐT", message: "Số điện thoại nhận hàng ạ?", collect_field: "phone", validation: "vietnam_phone" },
      { step_id: 6, name: "Xác nhận", message: "🧾 Đơn hàng của anh/chị:\n🍴 {items} x {quantity}\n📝 Ghi chú: {notes}\n📍 Giao đến: {address}\n📞 SĐT: {phone}\n\nAnh/chị xác nhận đặt không ạ?", collect_field: "confirmation", validation: "yes_no" },
    ],
    success_message: "Đặt hàng thành công! 🎉 Đơn hàng sẽ được giao trong 30-45 phút ạ. Cảm ơn anh/chị! 🙏",
    cancel_message: "Dạ không sao ạ! Khi nào anh/chị đói cứ gọi em nhé 😋",
  },
  {
    id: "lead-qualification",
    name: "Đánh Giá Khách Hàng Tiềm Năng",
    description: "Thu thập thông tin để đánh giá và chăm sóc khách hàng",
    icon: "🎯",
    goal: "consultation",
    trigger_phrases: ["quan tâm", "tìm hiểu", "muốn biết thêm", "cho hỏi", "tư vấn giúp"],
    steps: [
      { step_id: 1, name: "Tên", message: "Chào anh/chị! 😊 Em là trợ lý AI. Cho em xin tên của anh/chị để tiện xưng hô ạ?", collect_field: "name", validation: "non_empty" },
      { step_id: 2, name: "Nhu cầu", message: "Anh/chị {name} đang quan tâm đến sản phẩm/dịch vụ nào của bên em ạ?", collect_field: "interest", validation: "non_empty" },
      { step_id: 3, name: "Mục đích", message: "Anh/chị dự định sử dụng cho mục đích gì ạ? (cá nhân/kinh doanh/tặng quà...)", collect_field: "purpose", validation: "non_empty" },
      { step_id: 4, name: "Ngân sách", message: "Ngân sách dự kiến của anh/chị khoảng bao nhiêu ạ?", collect_field: "budget", validation: null },
      { step_id: 5, name: "Thời gian", message: "Anh/chị dự định mua/sử dụng trong thời gian nào ạ?", collect_field: "timeline", validation: "non_empty" },
      { step_id: 6, name: "SĐT", message: "Số điện thoại để nhân viên tư vấn liên hệ chi tiết ạ?", collect_field: "phone", validation: "vietnam_phone" },
    ],
    success_message: "Cảm ơn anh/chị {name}! 🙏 Em đã ghi nhận thông tin. Nhân viên tư vấn sẽ liên hệ trong 15 phút tới ạ!",
    cancel_message: "Dạ không sao ạ! Khi nào anh/chị cần tư vấn cứ nhắn em nhé 😊",
  },
  {
    id: "complaint-handling",
    name: "Xử Lý Khiếu Nại",
    description: "Tiếp nhận và xử lý phản hồi, khiếu nại của khách hàng",
    icon: "🔧",
    goal: "custom",
    trigger_phrases: ["khiếu nại", "phàn nàn", "không hài lòng", "có vấn đề", "lỗi", "hỏng", "trả hàng"],
    steps: [
      { step_id: 1, name: "Tên khách hàng", message: "Dạ em rất tiếc khi anh/chị gặp vấn đề 😔 Cho em xin tên của anh/chị ạ?", collect_field: "name", validation: "non_empty" },
      { step_id: 2, name: "Mã đơn hàng", message: "Anh/chị {name} cho em xin mã đơn hàng hoặc số điện thoại đặt hàng ạ?", collect_field: "order_id", validation: "non_empty" },
      { step_id: 3, name: "Mô tả vấn đề", message: "Anh/chị mô tả chi tiết vấn đề gặp phải giúp em ạ?", collect_field: "issue", validation: "non_empty" },
      { step_id: 4, name: "Hình ảnh", message: "Anh/chị có thể gửi hình ảnh/video (nếu có) để em kiểm tra ạ? (Gửi 'không' nếu không có)", collect_field: "evidence", validation: null },
      { step_id: 5, name: "Yêu cầu", message: "Anh/chị mong muốn được giải quyết như thế nào ạ?\n1️⃣ Đổi sản phẩm mới\n2️⃣ Hoàn tiền\n3️⃣ Sửa chữa/bảo hành\n4️⃣ Khác", collect_field: "resolution", validation: "non_empty" },
      { step_id: 6, name: "SĐT liên hệ", message: "Số điện thoại để bộ phận CSKH liên hệ xử lý ạ?", collect_field: "phone", validation: "vietnam_phone" },
    ],
    success_message: "Em đã tiếp nhận phản hồi của anh/chị {name} 🙏 Bộ phận CSKH sẽ liên hệ trong vòng 2 giờ để giải quyết. Một lần nữa em xin lỗi vì sự bất tiện này ạ!",
    cancel_message: "Dạ nếu anh/chị cần hỗ trợ thêm, cứ nhắn em bất cứ lúc nào nhé! 🙏",
  },
  {
    id: "feedback-collection",
    name: "Thu Thập Đánh Giá",
    description: "Lấy feedback và đánh giá từ khách hàng",
    icon: "⭐",
    goal: "info",
    trigger_phrases: ["đánh giá", "review", "feedback", "góp ý", "nhận xét"],
    steps: [
      { step_id: 1, name: "Tên", message: "Cảm ơn anh/chị đã sử dụng dịch vụ! 🙏 Cho em xin tên của anh/chị ạ?", collect_field: "name", validation: "non_empty" },
      { step_id: 2, name: "Điểm đánh giá", message: "Anh/chị {name} đánh giá trải nghiệm của mình bao nhiêu sao ạ?\n⭐ 1 sao - Rất không hài lòng\n⭐⭐ 2 sao - Không hài lòng\n⭐⭐⭐ 3 sao - Bình thường\n⭐⭐⭐⭐ 4 sao - Hài lòng\n⭐⭐⭐⭐⭐ 5 sao - Rất hài lòng", collect_field: "rating", validation: "non_empty" },
      { step_id: 3, name: "Điểm tốt", message: "Anh/chị thích nhất điều gì về sản phẩm/dịch vụ ạ?", collect_field: "positive", validation: null },
      { step_id: 4, name: "Điểm cần cải thiện", message: "Anh/chị có góp ý gì để bên em cải thiện không ạ?", collect_field: "improvement", validation: null },
      { step_id: 5, name: "Giới thiệu", message: "Anh/chị có sẵn lòng giới thiệu cho bạn bè không ạ? (có/không)", collect_field: "recommend", validation: "yes_no" },
    ],
    success_message: "Cảm ơn anh/chị {name} đã dành thời gian đánh giá! 🙏💕 Feedback của anh/chị rất quý giá với chúng em. Chúc anh/chị một ngày tốt lành!",
    cancel_message: "Dạ không sao ạ! Cảm ơn anh/chị đã sử dụng dịch vụ 🙏",
  },
  {
    id: "real-estate-inquiry",
    name: "Tư Vấn Bất Động Sản",
    description: "Thu thập nhu cầu mua/thuê bất động sản",
    icon: "🏠",
    goal: "consultation",
    trigger_phrases: ["mua nhà", "thuê nhà", "bất động sản", "căn hộ", "chung cư", "đất nền", "biệt thự"],
    steps: [
      { step_id: 1, name: "Loại hình", message: "Chào anh/chị! 🏠 Anh/chị đang tìm kiếm:\n1️⃣ Mua nhà/căn hộ\n2️⃣ Thuê nhà/căn hộ\n3️⃣ Mua đất nền\n4️⃣ Đầu tư BĐS", collect_field: "type", validation: "non_empty" },
      { step_id: 2, name: "Khu vực", message: "Anh/chị muốn tìm ở khu vực nào ạ?", collect_field: "location", validation: "non_empty" },
      { step_id: 3, name: "Diện tích", message: "Diện tích mong muốn khoảng bao nhiêu m² ạ?", collect_field: "area", validation: "non_empty" },
      { step_id: 4, name: "Ngân sách", message: "Ngân sách dự kiến của anh/chị là bao nhiêu ạ?", collect_field: "budget", validation: "non_empty" },
      { step_id: 5, name: "Yêu cầu khác", message: "Anh/chị có yêu cầu đặc biệt nào không ạ? (số phòng ngủ, hướng nhà, tiện ích...)", collect_field: "requirements", validation: null },
      { step_id: 6, name: "Tên", message: "Tên của anh/chị để em ghi nhận ạ?", collect_field: "name", validation: "non_empty" },
      { step_id: 7, name: "SĐT", message: "Số điện thoại để chuyên viên tư vấn liên hệ ạ?", collect_field: "phone", validation: "vietnam_phone" },
    ],
    success_message: "Cảm ơn anh/chị {name}! 🏠 Em đã ghi nhận nhu cầu. Chuyên viên tư vấn sẽ gọi điện trong 30 phút với các căn phù hợp ạ!",
    cancel_message: "Dạ không sao ạ! Khi nào anh/chị cần tư vấn BĐS cứ nhắn em nhé 🏠",
  },
  {
    id: "course-enrollment",
    name: "Đăng Ký Khóa Học",
    description: "Đăng ký khóa học, workshop, sự kiện",
    icon: "📚",
    goal: "order",
    trigger_phrases: ["đăng ký học", "khóa học", "học phí", "lịch học", "khai giảng", "workshop"],
    steps: [
      { step_id: 1, name: "Khóa học", message: "Chào anh/chị! 📚 Anh/chị quan tâm đến khóa học nào ạ?", collect_field: "course", validation: "non_empty" },
      { step_id: 2, name: "Tên học viên", message: "Tên đầy đủ của học viên ạ?", collect_field: "name", validation: "non_empty" },
      { step_id: 3, name: "Email", message: "Email để em gửi thông tin chi tiết khóa học ạ?", collect_field: "email", validation: "non_empty" },
      { step_id: 4, name: "SĐT", message: "Số điện thoại liên hệ ạ?", collect_field: "phone", validation: "vietnam_phone" },
      { step_id: 5, name: "Lịch học", message: "Anh/chị muốn học lịch nào ạ?\n🌅 Ca sáng (8h-11h)\n☀️ Ca chiều (14h-17h)\n🌙 Ca tối (18h-21h)\n💻 Online linh hoạt", collect_field: "schedule", validation: "non_empty" },
      { step_id: 6, name: "Xác nhận", message: "📋 Thông tin đăng ký:\n📚 Khóa học: {course}\n👤 Học viên: {name}\n📧 Email: {email}\n📞 SĐT: {phone}\n⏰ Lịch học: {schedule}\n\nAnh/chị xác nhận đăng ký không ạ?", collect_field: "confirmation", validation: "yes_no" },
    ],
    success_message: "Đăng ký thành công! 🎉 Em đã gửi email xác nhận và thông tin thanh toán. Chào mừng anh/chị {name} đến với khóa học! 📚",
    cancel_message: "Dạ không sao ạ! Khi nào anh/chị quyết định cứ nhắn em để được hỗ trợ đăng ký nhé 📚",
  },
];

const FAQ_CATEGORY_OPTIONS = [
  { value: "all", label: "All Conversations", shortLabel: "All" },
  { value: "direct", label: "Direct Messages Only", shortLabel: "Direct" },
  { value: "group", label: "Group Chats Only", shortLabel: "Group" },
];

const FAQ_LIST_FILTERS = [
  { value: "", label: "All Categories" },
  { value: "all", label: "All Conversations" },
  { value: "direct", label: "Direct Messages" },
  { value: "group", label: "Group Chats" },
];

const normalizeFAQVariation = (variation) => ({
  question: String(variation?.question || "").trim(),
  answer: String(variation?.answer || "").trim(),
});

const normalizeFAQ = (faq) => ({
  ...faq,
  category: faq?.category || "all",
  variations: Array.isArray(faq?.variations)
    ? faq.variations
        .map(normalizeFAQVariation)
        .filter((variation) => variation.question && variation.answer)
    : [],
});

const getVariationKey = (variation) => `${variation.question}::${variation.answer}`;

const getFAQCategoryMeta = (category) => {
  const option = FAQ_CATEGORY_OPTIONS.find((item) => item.value === category) || FAQ_CATEGORY_OPTIONS[0];
  const colors = {
    all: "bg-white/10 text-white/70",
    direct: "bg-sky-500/20 text-sky-300",
    group: "bg-purple-500/20 text-purple-300",
  };

  return {
    ...option,
    colorClass: colors[option.value] || colors.all,
  };
};

const FAQCategoryBadge = ({ category }) => {
  const meta = getFAQCategoryMeta(category);
  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${meta.colorClass}`}>
      {meta.shortLabel}
    </span>
  );
};

const FAQCategoryField = ({ value, onChange }) => (
  <div>
    <label className="block text-xs font-medium text-white/60 mb-1">
      Where should this answer be used?
    </label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-4 py-2.5 text-sm bg-[#050810] border border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ff4d4d] text-white"
    >
      {FAQ_CATEGORY_OPTIONS.map((option) => (
        <option key={option.value} value={option.value} className="bg-[#0d1424]">
          {option.label}
        </option>
      ))}
    </select>
    <p className="mt-1 text-xs text-white/35">
      Choose whether this FAQ applies everywhere, only in direct messages, or only in group chats.
    </p>
  </div>
);

// FAQ Item Component
const FAQItem = ({
  faq,
  index,
  isEditing,
  onEdit,
  onSave,
  onCancel,
  onDelete,
  onToggleExpand,
  isExpanded,
  onRemoveVariation,
  onRegenerateVariations,
  isUpdatingVariations,
}) => {
  const [editQuestion, setEditQuestion] = useState(faq.question);
  const [editAnswer, setEditAnswer] = useState(faq.answer);
  const [editCategory, setEditCategory] = useState(faq.category || "all");

  useEffect(() => {
    setEditQuestion(faq.question);
    setEditAnswer(faq.answer);
    setEditCategory(faq.category || "all");
  }, [faq]);

  const handleSave = () => {
    onSave(index, { question: editQuestion, answer: editAnswer, category: editCategory });
  };

  if (isEditing) {
    return (
      <div className="p-4 bg-[#0d1424] rounded-xl border-2 border-[#ff4d4d] shadow-lg">
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-white/50 mb-1">
              Question
            </label>
            <input
              type="text"
              value={editQuestion}
              onChange={(e) => setEditQuestion(e.target.value)}
              placeholder="Enter the question..."
              className="w-full px-4 py-2.5 text-sm bg-[#050810] border border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ff4d4d] text-white placeholder-white/30"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-white/50 mb-1">
              Answer
            </label>
            <textarea
              value={editAnswer}
              onChange={(e) => setEditAnswer(e.target.value)}
              placeholder="Enter the answer..."
              rows={4}
              className="w-full px-4 py-2.5 text-sm bg-[#050810] border border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ff4d4d] text-white placeholder-white/30 resize-none"
            />
          </div>
          <FAQCategoryField value={editCategory} onChange={setEditCategory} />
          <div className="flex items-center justify-end gap-2">
            <button
              onClick={onCancel}
              className="px-4 py-2 text-sm text-white/60 hover:bg-white/5 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!editQuestion.trim() || !editAnswer.trim()}
              className="px-4 py-2 text-sm bg-[#ff4d4d] hover:bg-[#ff5c5c] text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
            >
              <Save className="w-4 h-4" />
              Save
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="group bg-[#0a0f1c] rounded-xl border border-white/10 hover:border-white/20 transition-all">
      <button
        onClick={() => onToggleExpand(index)}
        className="w-full p-4 flex items-start gap-3 text-left"
      >
        <div className="flex-shrink-0 mt-0.5">
          <div className="w-6 h-6 rounded-full bg-[#ff4d4d]/20 text-[#ff4d4d] flex items-center justify-center text-xs font-bold">
            {index + 1}
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-medium text-white">
              {faq.question}
            </p>
            <FAQCategoryBadge category={faq.category} />
            {faq.status && (
              <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-emerald-500/15 text-emerald-300">
                {faq.status}
              </span>
            )}
          </div>
          <div className="mt-1 text-xs text-white/35 flex items-center gap-3">
            <span>Updated {faq.updated_at ? new Date(faq.updated_at).toLocaleDateString() : "recently"}</span>
          </div>
          {isExpanded && (
            <div className="mt-3 space-y-4">
              <p className="text-sm text-white/60 whitespace-pre-wrap">
                {faq.answer}
              </p>
              <div className="rounded-xl border border-white/10 bg-[#050810]/80 p-3">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div>
                    <p className="text-sm font-medium text-white">Saved Variations</p>
                    <p className="text-xs text-white/40">
                      Alternate question and answer pairs the bot can also match against.
                    </p>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onRegenerateVariations(index);
                    }}
                    disabled={isUpdatingVariations}
                    className="px-3 py-1.5 text-xs bg-white/5 hover:bg-white/10 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
                  >
                    {isUpdatingVariations ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                    Regenerate
                  </button>
                </div>
                {faq.variations?.length ? (
                  <div className="space-y-2">
                    {faq.variations.map((variation) => {
                      const variationKey = getVariationKey(variation);
                      return (
                        <div key={variationKey} className="rounded-lg border border-white/10 bg-white/[0.03] p-3">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="text-xs font-medium uppercase tracking-wide text-white/35">
                                Variation
                              </p>
                              <p className="mt-1 text-sm text-white">{variation.question}</p>
                              <p className="mt-2 text-sm text-white/60 whitespace-pre-wrap">{variation.answer}</p>
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onRemoveVariation(index, variationKey);
                              }}
                              disabled={isUpdatingVariations}
                              className="p-1.5 text-white/40 hover:text-red-400 hover:bg-white/5 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-white/45">
                    No saved variations yet. Generate alternates to help the bot recognize more phrasings.
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit(index);
            }}
            className="p-1.5 text-white/40 hover:text-[#ff4d4d] hover:bg-white/5 rounded-lg opacity-0 group-hover:opacity-100 transition-all"
          >
            <Edit3 className="w-4 h-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete(index);
            }}
            className="p-1.5 text-white/40 hover:text-red-400 hover:bg-white/5 rounded-lg opacity-0 group-hover:opacity-100 transition-all"
          >
            <Trash2 className="w-4 h-4" />
          </button>
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-white/40" />
          ) : (
            <ChevronDown className="w-5 h-5 text-white/40" />
          )}
        </div>
      </button>
    </div>
  );
};

// Add FAQ Form Component
const AddFAQForm = ({ onAdd, onCancel }) => {
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [category, setCategory] = useState("all");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (question.trim() && answer.trim()) {
      const didStartFlow = await onAdd({ question: question.trim(), answer: answer.trim(), category });
      if (didStartFlow !== false) {
        setQuestion("");
        setAnswer("");
        setCategory("all");
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} className="p-4 bg-[#ff4d4d]/10 rounded-xl border-2 border-dashed border-[#ff4d4d]/30">
      <div className="space-y-4">
        <div>
          <label className="block text-xs font-medium text-white/60 mb-1">
            Question
          </label>
          <input
            type="text"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="What question will customers ask?"
            className="w-full px-4 py-2.5 text-sm bg-[#050810] border border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ff4d4d] text-white placeholder-white/30"
            autoFocus
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-white/60 mb-1">
            Answer
          </label>
          <textarea
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            placeholder="How should the bot respond?"
            rows={4}
            className="w-full px-4 py-2.5 text-sm bg-[#050810] border border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ff4d4d] text-white placeholder-white/30 resize-none"
          />
        </div>
        <FAQCategoryField value={category} onChange={setCategory} />
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm text-white/60 hover:bg-white/5 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!question.trim() || !answer.trim()}
            className="px-4 py-2 text-sm bg-[#ff4d4d] hover:bg-[#ff5c5c] text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add FAQ
          </button>
        </div>
      </div>
    </form>
  );
};

const FAQVariationModal = ({
  isOpen,
  mode,
  draft,
  generatedVariations,
  selectedVariationKeys,
  onToggleVariation,
  onUpdateVariation,
  onSave,
  onSaveWithoutVariations,
  onRegenerate,
  onCancel,
  isGenerating,
  isSaving,
  error,
}) => {
  if (!isOpen || !draft) {
    return null;
  }

  const hasVariations = generatedVariations.length > 0;
  const title = mode === "edit" ? "Replace FAQ Variations" : "Choose FAQ Variations";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-2xl border border-white/10 bg-[#050810] shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-white/10 px-6 py-5">
          <div>
            <h3 className="text-lg font-semibold text-white">{title}</h3>
            <p className="mt-1 text-sm text-white/50">
              We generated alternate versions of this FAQ. Select and edit any you want the bot to recognize.
            </p>
          </div>
          <button
            onClick={onCancel}
            disabled={isSaving}
            className="text-white/50 hover:text-white disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="max-h-[calc(90vh-152px)] overflow-y-auto px-6 py-5">
          <div className="rounded-xl border border-white/10 bg-[#0a0f1c] p-4 mb-5">
            <div className="flex items-center gap-2 mb-2">
              <FAQCategoryBadge category={draft.category} />
              <span className="text-xs text-white/35">Base FAQ</span>
            </div>
            <p className="text-sm font-medium text-white">{draft.question}</p>
            <p className="mt-2 text-sm text-white/60 whitespace-pre-wrap">{draft.answer}</p>
          </div>

          {error && (
            <div className="mb-5 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-200">
              {error}
            </div>
          )}

          {isGenerating ? (
            <div className="flex flex-col items-center justify-center py-16 text-white/50">
              <Loader2 className="w-8 h-8 animate-spin text-[#ff4d4d]" />
              <p className="mt-4">Generating 5 alternate FAQ variations...</p>
            </div>
          ) : hasVariations ? (
            <div className="grid gap-3 md:grid-cols-2">
              {generatedVariations.map((variation, index) => {
                const variationKey = getVariationKey(variation);
                const selected = selectedVariationKeys.includes(variationKey);

                return (
                  <div
                    key={variationKey}
                    className={`rounded-xl border p-4 transition-colors ${
                      selected
                        ? "border-[#ff4d4d] bg-[#ff4d4d]/10"
                        : "border-white/10 bg-[#0a0f1c]"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3 mb-3">
                      <span className="text-xs font-medium uppercase tracking-wide text-white/35">
                        Variation {index + 1}
                      </span>
                      <button
                        type="button"
                        onClick={() => onToggleVariation(variationKey)}
                        className={`inline-flex h-5 w-5 items-center justify-center rounded-full border transition-colors ${
                          selected
                            ? "border-[#ff4d4d] bg-[#ff4d4d] text-white"
                            : "border-white/20 text-transparent hover:border-white/40"
                        }`}
                      >
                        <Check className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={variation.question}
                        onChange={(e) => onUpdateVariation(index, "question", e.target.value)}
                        placeholder="Question"
                        className="w-full px-3 py-2 text-sm font-medium text-white bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-[#ff4d4d]/50 placeholder-white/30"
                      />
                      <textarea
                        value={variation.answer}
                        onChange={(e) => onUpdateVariation(index, "answer", e.target.value)}
                        placeholder="Answer"
                        rows={3}
                        className="w-full px-3 py-2 text-sm text-white/80 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-[#ff4d4d]/50 placeholder-white/30 resize-none"
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="rounded-xl border border-white/10 bg-[#0a0f1c] px-4 py-6 text-sm text-white/50">
              We couldn't generate FAQ variations right now. You can save this FAQ without variations or try again.
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-white/10 px-6 py-4">
          <button
            onClick={onCancel}
            disabled={isSaving}
            className="px-4 py-2 text-sm text-white/60 hover:bg-white/5 rounded-lg disabled:opacity-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onRegenerate}
            disabled={isGenerating || isSaving}
            className="px-4 py-2 text-sm bg-white/5 hover:bg-white/10 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
          >
            {isGenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            Regenerate
          </button>
          <button
            onClick={onSaveWithoutVariations}
            disabled={isSaving}
            className="px-4 py-2 text-sm bg-white/5 hover:bg-white/10 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Save Without Variations
          </button>
          <button
            onClick={onSave}
            disabled={isSaving}
            className="px-4 py-2 text-sm bg-[#ff4d4d] hover:bg-[#ff5c5c] text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
          >
            {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save FAQ
          </button>
        </div>
      </div>
    </div>
  );
};

// Toggle Switch Component
const ToggleSwitch = ({ enabled, onChange, disabled, isLoading }) => {
  return (
    <button
      onClick={() => !disabled && !isLoading && onChange(!enabled)}
      disabled={disabled || isLoading}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#ff4d4d] focus:ring-offset-2 focus:ring-offset-[#050810] ${
        disabled || isLoading ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
      } ${enabled ? 'bg-emerald-500' : 'bg-white/20'}`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          enabled ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
      {isLoading && (
        <span className="absolute inset-0 flex items-center justify-center">
          <Loader2 className="w-3 h-3 animate-spin text-white" />
        </span>
      )}
    </button>
  );
};

// Channel Badge Component
const ChannelBadge = ({ channel }) => {
  const channelConfig = {
    zalo: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'Zalo' },
    telegram: { bg: 'bg-sky-500/20', text: 'text-sky-400', label: 'Telegram' },
    facebook: { bg: 'bg-indigo-500/20', text: 'text-indigo-400', label: 'Facebook' },
    zalouser: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'Zalo User' },
    whatsapp: { bg: 'bg-green-500/20', text: 'text-green-400', label: 'WhatsApp' },
  };

  const config = channelConfig[channel?.toLowerCase()] || { bg: 'bg-white/10', text: 'text-white/60', label: channel };

  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  );
};

// Agents Tab Content
const AgentsTab = () => {
  const [agents, setAgents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [togglingAgent, setTogglingAgent] = useState(null);

  // Fetch bot agents on mount
  useEffect(() => {
    const loadAgents = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const result = await fetchBotAgents();
        setAgents(result.agents || []);
      } catch (err) {
        console.error('[AgentsTab] Failed to load agents:', err);
        setError('Failed to load agents. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    loadAgents();
  }, []);

  const handleToggleAgent = async (agentName, enabled) => {
    setTogglingAgent(agentName);
    try {
      const result = await toggleBotAgent(agentName, enabled);

      // Update local state
      setAgents(agents.map(agent =>
        agent.name === agentName
          ? { ...agent, enabled: result.enabled, bindings: result.bindings }
          : agent
      ));
    } catch (err) {
      console.error('[AgentsTab] Failed to toggle agent:', err);
      setError(`Failed to ${enabled ? 'enable' : 'disable'} agent. ${err.message}`);
    } finally {
      setTogglingAgent(null);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="h-full flex flex-col items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#ff4d4d]" />
        <p className="mt-4 text-white/50">Loading agents...</p>
      </div>
    );
  }

  // Error state
  if (error && agents.length === 0) {
    return (
      <div className="h-full flex flex-col items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <p className="text-red-400 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-[#ff4d4d] hover:bg-[#ff5c5c] text-white rounded-lg"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Error banner */}
      {error && (
        <div className="mb-4 p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 text-sm flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-300">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-white">
            Bot Agents
          </h2>
          <p className="text-sm text-white/50">
            Enable or disable your AI agents. Disabled agents stop responding to all conversations.
          </p>
        </div>
      </div>

      {/* Agents List */}
      <div className="flex-1 min-h-0 overflow-y-auto space-y-3 pr-1">
        {agents.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-white/50">
            <Bot className="w-12 h-12 mb-3 opacity-50" />
            <p className="text-lg font-medium">No agents found</p>
            <p className="text-sm">Configure your agents to get started</p>
          </div>
        ) : (
          agents.map((agent) => (
            <div
              key={agent.name}
              className={`p-4 bg-[#0a0f1c] rounded-xl border transition-all ${
                agent.enabled ? 'border-emerald-500/30' : 'border-white/10'
              }`}
            >
              <div className="flex items-start justify-between gap-4">
                {/* Agent Info */}
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    agent.enabled ? 'bg-emerald-500/20' : 'bg-white/10'
                  }`}>
                    <Bot className={`w-5 h-5 ${agent.enabled ? 'text-emerald-400' : 'text-white/40'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-medium text-white truncate">
                        {agent.display_name || agent.name}
                      </h3>
                      {agent.is_default && (
                        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-[#ff4d4d]/20 text-[#ff4d4d]">
                          Default
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-white/40 mt-0.5 truncate">
                      {agent.name}
                    </p>
                    {agent.model && (
                      <p className="text-xs text-white/30 mt-1 truncate flex items-center gap-1">
                        <Zap className="w-3 h-3" />
                        {agent.model}
                      </p>
                    )}

                    {/* Channel Bindings */}
                    {agent.bindings && agent.bindings.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {agent.bindings.map((binding, idx) => (
                          <ChannelBadge key={idx} channel={binding.channel} />
                        ))}
                      </div>
                    )}

                    {/* Disabled message */}
                    {!agent.enabled && (
                      <p className="text-xs text-amber-400/70 mt-2 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        Agent is disabled and not responding to messages
                      </p>
                    )}
                  </div>
                </div>

                {/* Toggle Switch */}
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  <ToggleSwitch
                    enabled={agent.enabled}
                    onChange={(enabled) => handleToggleAgent(agent.name, enabled)}
                    isLoading={togglingAgent === agent.name}
                  />
                  <span className={`text-xs ${agent.enabled ? 'text-emerald-400' : 'text-white/40'}`}>
                    {agent.enabled ? 'Active' : 'Disabled'}
                  </span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Stats Footer */}
      <div className="mt-4 pt-4 border-t border-white/10">
        <div className="flex items-center justify-between text-sm text-white/50">
          <span>
            {agents.filter(a => a.enabled).length} of {agents.length} agent{agents.length !== 1 ? 's' : ''} active
          </span>
          <span className="flex items-center gap-1">
            <Power className="w-4 h-4" />
            Toggle to enable/disable agents
          </span>
        </div>
      </div>
    </div>
  );
};

// FAQs Tab Content
const FAQsTab = () => {
  const [faqs, setFaqs] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isAddingFAQ, setIsAddingFAQ] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [isPublishing, setIsPublishing] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [accountId, setAccountId] = useState(null);
  const [activeAgentId, setActiveAgentId] = useState(null);
  const [activeAgentLabel, setActiveAgentLabel] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [isBulkImporting, setIsBulkImporting] = useState(false);
  const [bulkInput, setBulkInput] = useState("");
  // Knowledge generation options
  const [generateKnowledgeOnPublish, setGenerateKnowledgeOnPublish] = useState(true);
  const [knowledgeGenerationStatus, setKnowledgeGenerationStatus] = useState(null); // 'generating' | 'success' | 'error' | null
  const [variationFlow, setVariationFlow] = useState({
    isOpen: false,
    mode: "create",
    faqIndex: null,
    draft: null,
    generatedVariations: [],
    selectedVariationKeys: [],
    isGenerating: false,
    isSaving: false,
    error: null,
  });
  const [variationUpdateIndex, setVariationUpdateIndex] = useState(null);

  const loadFAQs = async (acctId, agentId, category = categoryFilter) => {
    const faqsResult = await fetchClaw9FAQs(acctId, agentId, category || undefined);
    const loadedFaqs = (faqsResult.data?.faqs || faqsResult.faqs || []).map(normalizeFAQ);
    setFaqs(loadedFaqs);
    const hasDrafts = loadedFaqs.some((f) => f.status === "draft");
    setHasChanges(hasDrafts);
    return loadedFaqs;
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const [accountsResult, agentsResult] = await Promise.all([
          fetchClaw9Accounts(),
          fetchBotAgents(),
        ]);
        const accounts = accountsResult.accounts || [];
        const agents = agentsResult.agents || [];

        if (accounts.length > 0) {
          const acctId = accounts[0].account_id;
          setAccountId(acctId);
        } else {
          setError('No accounts found. Please configure your account first.');
          return;
        }

        if (agents.length > 0) {
          const defaultAgent = agents.find((agent) => agent.is_default) || agents[0];
          setActiveAgentId(defaultAgent.name);
          setActiveAgentLabel(defaultAgent.display_name || defaultAgent.name);
        } else {
          setError('No bot agents found. Please configure an agent first.');
          return;
        }

        const acctId = accounts[0].account_id;
        const defaultAgent = agents.find((agent) => agent.is_default) || agents[0];

        try {
          await loadFAQs(acctId, defaultAgent.name, "");
        } catch (faqError) {
          console.log('[Claw9Agents] FAQs not found, starting fresh:', faqError);
          setFaqs([]);
        }
      } catch (err) {
        console.error('[Claw9Agents] Failed to load data:', err);
        setError('Failed to load FAQ workspace. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    if (!accountId || !activeAgentId) return;

    const refreshFilteredFAQs = async () => {
      try {
        setIsLoading(true);
        setError(null);
        await loadFAQs(accountId, activeAgentId, categoryFilter);
      } catch (err) {
        console.error('[Claw9Agents] Failed to filter FAQs:', err);
        setError('Failed to load FAQs. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    refreshFilteredFAQs();
  }, [accountId, activeAgentId, categoryFilter]);

  const filteredFaqs = faqs.filter(
    (faq) =>
      faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const closeVariationFlow = () => {
    setVariationFlow({
      isOpen: false,
      mode: "create",
      faqIndex: null,
      draft: null,
      generatedVariations: [],
      selectedVariationKeys: [],
      isGenerating: false,
      isSaving: false,
      error: null,
    });
  };

  const generateVariationsForDraft = async (draft, mode = "create", faqIndex = null) => {
    if (!accountId || !activeAgentId) {
      return;
    }

    setVariationFlow((current) => ({
      ...current,
      isOpen: true,
      mode,
      faqIndex,
      draft,
      isGenerating: true,
      error: null,
    }));

    try {
      const result = await generateClaw9FAQVariations(accountId, activeAgentId, {
        question: draft.question,
        answer: draft.answer,
        category: draft.category,
        count: 5,
      });
      const generatedVariations = (result.data?.variations || result.variations || [])
        .map(normalizeFAQVariation)
        .filter((variation) => variation.question && variation.answer);

      setVariationFlow((current) => ({
        ...current,
        generatedVariations,
        selectedVariationKeys: [],
        isGenerating: false,
        error: generatedVariations.length ? null : "We couldn’t generate FAQ variations right now.",
      }));
    } catch (err) {
      console.error('[Claw9Agents] Failed to generate FAQ variations:', err);
      setVariationFlow((current) => ({
        ...current,
        generatedVariations: [],
        selectedVariationKeys: [],
        isGenerating: false,
        error: "We couldn’t generate FAQ variations right now.",
      }));
    }
  };

  const persistFAQWithVariations = async (selectedVariations) => {
    if (!accountId || !activeAgentId || !variationFlow.draft) {
      return;
    }

    const payload = {
      question: variationFlow.draft.question,
      answer: variationFlow.draft.answer,
      category: variationFlow.draft.category,
      variations: selectedVariations,
    };

    setVariationFlow((current) => ({ ...current, isSaving: true, error: null }));

    try {
      if (variationFlow.mode === "edit" && variationFlow.faqIndex !== null) {
        const existingFaq = faqs[variationFlow.faqIndex];
        const result = await updateClaw9FAQ(accountId, activeAgentId, existingFaq.id, payload);
        const updated = normalizeFAQ(result.data || result);
        setFaqs((current) => current.map((faq, index) => (
          index === variationFlow.faqIndex ? { ...faq, ...updated } : faq
        )));
      } else {
        const result = await createClaw9FAQ(accountId, activeAgentId, payload);
        const createdFaq = normalizeFAQ(result.data || result);
        setFaqs((current) => [...current, createdFaq]);
        setIsAddingFAQ(false);
      }

      setHasChanges(true);
      closeVariationFlow();
    } catch (err) {
      console.error('[Claw9Agents] Failed to save FAQ with variations:', err);
      setVariationFlow((current) => ({
        ...current,
        isSaving: false,
        error: err.message || 'Failed to save FAQ. Please try again.',
      }));
    }
  };

  const handleAddFAQ = async (newFaq) => {
    if (!accountId || !activeAgentId) {
      return false;
    }

    await generateVariationsForDraft(newFaq, "create", null);
    return true;
  };

  const handleToggleVariation = (variationKey) => {
    setVariationFlow((current) => ({
      ...current,
      selectedVariationKeys: current.selectedVariationKeys.includes(variationKey)
        ? current.selectedVariationKeys.filter((key) => key !== variationKey)
        : [...current.selectedVariationKeys, variationKey],
    }));
  };

  const handleUpdateVariation = (index, field, value) => {
    setVariationFlow((current) => ({
      ...current,
      generatedVariations: current.generatedVariations.map((v, i) =>
        i === index ? { ...v, [field]: value } : v
      ),
    }));
  };

  const handleSaveGeneratedFAQ = async () => {
    const selectedVariations = variationFlow.generatedVariations.filter((variation) =>
      variationFlow.selectedVariationKeys.includes(getVariationKey(variation))
    );
    await persistFAQWithVariations(selectedVariations);
  };

  const handleSaveWithoutVariations = async () => {
    await persistFAQWithVariations([]);
  };

  const handleSaveFAQ = async (index, updatedFaq) => {
    if (!accountId || !activeAgentId) return;

    const faq = faqs[index];
    if (!faq?.id) return;

    setIsPublishing(true);
    try {
      const result = await updateClaw9FAQ(accountId, activeAgentId, faq.id, {
        ...updatedFaq,
        variations: faq.variations || [],
      });
      const updated = normalizeFAQ(result.data || result);
      const newFaqs = [...faqs];
      newFaqs[index] = { ...faq, ...updated };
      setFaqs(newFaqs);
      setEditingIndex(null);
      setHasChanges(true);
    } catch (err) {
      console.error('[Claw9Agents] Failed to update FAQ:', err);
      setError(err.message || 'Failed to save FAQ. Please try again.');
    } finally {
      setIsPublishing(false);
    }
  };

  const handleDeleteFAQ = async (index) => {
    if (!accountId || !activeAgentId) return;

    const faq = faqs[index];

    if (!faq?.id) {
      return;
    }

    setIsPublishing(true);
    try {
      await deleteClaw9FAQ(accountId, activeAgentId, faq.id);
      setFaqs(faqs.filter((_, i) => i !== index));
      setHasChanges(true);
    } catch (err) {
      console.error('[Claw9Agents] Failed to delete FAQ:', err);
      setError(err.message || 'Failed to delete FAQ. Please try again.');
    } finally {
      setIsPublishing(false);
    }
  };

  const handleRemoveVariation = async (index, variationKey) => {
    if (!accountId || !activeAgentId) return;

    const faq = faqs[index];
    if (!faq?.id) return;

    const remainingVariations = (faq.variations || []).filter(
      (variation) => getVariationKey(variation) !== variationKey
    );

    setVariationUpdateIndex(index);
    try {
      const result = await updateClaw9FAQ(accountId, activeAgentId, faq.id, {
        variations: remainingVariations,
      });
      const updated = normalizeFAQ(result.data || result);
      setFaqs((current) => current.map((item, itemIndex) => (
        itemIndex === index ? { ...item, ...updated } : item
      )));
      setHasChanges(true);
    } catch (err) {
      console.error('[Claw9Agents] Failed to remove FAQ variation:', err);
      setError(err.message || 'Failed to update FAQ variations. Please try again.');
    } finally {
      setVariationUpdateIndex(null);
    }
  };

  const handleRegenerateVariations = async (index) => {
    const faq = faqs[index];
    if (!faq) return;
    await generateVariationsForDraft({
      question: faq.question,
      answer: faq.answer,
      category: faq.category || "all",
    }, "edit", index);
  };

  const handlePublish = async () => {
    if (!accountId || !activeAgentId) {
      console.error('[Claw9Agents] Cannot publish - missing account or agent ID');
      return;
    }

    setIsPublishing(true);
    setError(null);
    setKnowledgeGenerationStatus(null);

    // Longer timeout when generating knowledge (60 seconds)
    const timeoutMs = generateKnowledgeOnPublish ? 60000 : 30000;
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Publishing timed out. Please try again.')), timeoutMs)
    );

    try {
      if (generateKnowledgeOnPublish) {
        // Enhanced publish with knowledge generation
        setKnowledgeGenerationStatus('generating');
        const result = await Promise.race([
          publishFAQsWithKnowledge(accountId, activeAgentId, null, {
            generate_knowledge: true,
            sync_s3: true,
          }),
          timeoutPromise
        ]);

        if (result.knowledge) {
          setKnowledgeGenerationStatus('success');
          console.log('[Claw9Agents] Knowledge generated:', result.knowledge);
          console.log('[Claw9Agents] S3 URL:', result.s3_url);
        }
      } else {
        // Standard publish without knowledge generation
        await Promise.race([
          publishClaw9FAQs(accountId, activeAgentId),
          timeoutPromise
        ]);
      }

      setHasChanges(false);
      await loadFAQs(accountId, activeAgentId, categoryFilter);

      // Clear success status after 5 seconds
      if (knowledgeGenerationStatus === 'success') {
        setTimeout(() => setKnowledgeGenerationStatus(null), 5000);
      }

    } catch (err) {
      console.error("[Claw9Agents] Failed to publish FAQs:", err);
      setError(err.message || 'Failed to publish FAQs. Please try again.');
      if (generateKnowledgeOnPublish) {
        setKnowledgeGenerationStatus('error');
      }
    } finally {
      setIsPublishing(false);
    }
  };

  const handleBulkImport = async () => {
    if (!accountId || !activeAgentId) return;

    let parsed;
    try {
      parsed = JSON.parse(bulkInput);
    } catch {
      setError('Bulk import must be valid JSON.');
      return;
    }

    const items = Array.isArray(parsed) ? parsed : parsed?.faqs;
    if (!Array.isArray(items) || items.length === 0) {
      setError('Bulk import must include a non-empty FAQ array.');
      return;
    }

    const normalizedFaqs = items.map((item) => ({
      question: String(item.question || "").trim(),
      answer: String(item.answer || "").trim(),
      category: item.category || "all",
    }));

    if (normalizedFaqs.some((item) => !item.question || !item.answer)) {
      setError('Every imported FAQ needs both question and answer.');
      return;
    }

    setIsPublishing(true);
    setError(null);
    try {
      await bulkCreateClaw9FAQs(accountId, activeAgentId, normalizedFaqs, false);
      setBulkInput("");
      setIsBulkImporting(false);
      await loadFAQs(accountId, activeAgentId, categoryFilter);
      setHasChanges(true);
    } catch (err) {
      console.error('[Claw9Agents] Failed to bulk import FAQs:', err);
      setError(err.message || 'Failed to import FAQs. Please try again.');
    } finally {
      setIsPublishing(false);
    }
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="h-full flex flex-col items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#ff4d4d]" />
        <p className="mt-4 text-white/50">Loading FAQs...</p>
      </div>
    );
  }

  // Show error state
  if (error && !accountId) {
    return (
      <div className="h-full flex flex-col items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-[#ff4d4d] hover:bg-[#ff5c5c] text-white rounded-lg"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Error banner */}
      {error && (
        <div className="mb-4 p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 text-sm flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-300">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Success banner for knowledge generation */}
      {knowledgeGenerationStatus === 'success' && (
        <div className="mb-4 p-4 bg-emerald-500/20 border border-emerald-500/30 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-500/30 rounded-full">
              <Check className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <p className="text-emerald-300 font-medium">Knowledge Base Updated Successfully!</p>
              <p className="text-emerald-400/70 text-sm">Your AI knowledge base has been regenerated and synced.</p>
            </div>
          </div>
          <button
            onClick={() => setKnowledgeGenerationStatus(null)}
            className="text-emerald-400 hover:text-emerald-300 p-1"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-white">
            FAQ Knowledge Base
          </h2>
          <p className="text-sm text-white/50">
            Add questions and answers to train your bot. Use categories to control whether an answer is used in direct messages, group chats, or both.
          </p>
          {activeAgentLabel && (
            <p className="mt-1 text-xs text-white/35">
              Active agent: {activeAgentLabel}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          {/* Knowledge generation toggle */}
          <label className="flex items-center gap-2 cursor-pointer group">
            <input
              type="checkbox"
              checked={generateKnowledgeOnPublish}
              onChange={(e) => setGenerateKnowledgeOnPublish(e.target.checked)}
              className="sr-only"
            />
            <div className={`relative w-10 h-5 rounded-full transition-colors ${
              generateKnowledgeOnPublish ? 'bg-purple-600' : 'bg-white/20'
            }`}>
              <div className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
                generateKnowledgeOnPublish ? 'translate-x-5' : 'translate-x-0'
              }`} />
            </div>
            <span className="text-xs text-white/50 group-hover:text-white/70 flex items-center gap-1">
              <Sparkles className="w-3 h-3" />
              Generate AI Knowledge
            </span>
          </label>

          {/* Knowledge generation status */}
          {knowledgeGenerationStatus === 'generating' && (
            <span className="text-xs text-purple-400 bg-purple-500/20 px-2 py-1 rounded-full flex items-center gap-1">
              <Sparkles className="w-3 h-3 animate-pulse" />
              Generating...
            </span>
          )}
          {knowledgeGenerationStatus === 'success' && (
            <span className="text-xs text-emerald-400 bg-emerald-500/20 px-2 py-1 rounded-full flex items-center gap-1">
              <Check className="w-3 h-3" />
              Knowledge Updated
            </span>
          )}
          {knowledgeGenerationStatus === 'error' && (
            <span className="text-xs text-red-400 bg-red-500/20 px-2 py-1 rounded-full flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              Generation Failed
            </span>
          )}

          {hasChanges && (
            <span className="text-xs text-amber-400 bg-amber-500/20 px-2 py-1 rounded-full">
              Unpublished changes
            </span>
          )}
          <button
            onClick={handlePublish}
            disabled={isPublishing || !hasChanges || !accountId}
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
          >
            {isPublishing ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {generateKnowledgeOnPublish ? 'Publishing & Generating...' : 'Publishing...'}
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" />
                Publish
              </>
            )}
          </button>
        </div>
      </div>

      {/* Search and Add */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search FAQs..."
            className="w-full pl-10 pr-4 py-2.5 text-sm bg-[#0a0f1c] border border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ff4d4d] text-white placeholder-white/30"
          />
        </div>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="px-4 py-2.5 text-sm bg-[#0a0f1c] border border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ff4d4d] text-white"
        >
          {FAQ_LIST_FILTERS.map((option) => (
            <option key={option.value || "all-categories"} value={option.value} className="bg-[#0d1424]">
              {option.label}
            </option>
          ))}
        </select>
        <button
          onClick={() => setIsBulkImporting((value) => !value)}
          className="px-4 py-2.5 bg-white/5 hover:bg-white/10 text-white rounded-lg font-medium flex items-center gap-2 transition-colors"
        >
          <Upload className="w-4 h-4" />
          Bulk Import
        </button>
        {!isAddingFAQ && (
          <button
            onClick={() => setIsAddingFAQ(true)}
            className="px-4 py-2.5 bg-[#ff4d4d] hover:bg-[#ff5c5c] text-white rounded-lg font-medium flex items-center gap-2 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add FAQ
          </button>
        )}
      </div>

      {isBulkImporting && (
        <div className="mb-4 p-4 bg-[#0a0f1c] rounded-xl border border-white/10">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div>
              <h3 className="text-sm font-medium text-white">Bulk Import FAQs</h3>
              <p className="text-xs text-white/45">
                Paste a JSON array of FAQs. If category is omitted, send `all`.
              </p>
            </div>
            <button
              onClick={() => setIsBulkImporting(false)}
              className="text-white/50 hover:text-white"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <textarea
            value={bulkInput}
            onChange={(e) => setBulkInput(e.target.value)}
            rows={8}
            placeholder={`[\n  {\n    "question": "How do I respond in DMs?",\n    "answer": "Thanks for messaging us directly.",\n    "category": "direct"\n  }\n]`}
            className="w-full px-4 py-3 text-sm bg-[#050810] border border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ff4d4d] text-white placeholder-white/25 font-mono resize-none"
          />
          <div className="mt-3 flex items-center justify-end gap-2">
            <button
              onClick={() => {
                setBulkInput("");
                setIsBulkImporting(false);
              }}
              className="px-4 py-2 text-sm text-white/60 hover:bg-white/5 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleBulkImport}
              disabled={isPublishing || !bulkInput.trim()}
              className="px-4 py-2 text-sm bg-[#ff4d4d] hover:bg-[#ff5c5c] text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
            >
              <Upload className="w-4 h-4" />
              Import FAQs
            </button>
          </div>
        </div>
      )}

      {/* Add FAQ Form */}
      {isAddingFAQ && (
        <div className="mb-4">
          <AddFAQForm
            onAdd={handleAddFAQ}
            onCancel={() => setIsAddingFAQ(false)}
          />
        </div>
      )}

      {/* FAQ List */}
      <div className="flex-1 min-h-0 overflow-y-auto space-y-3 pr-1">
        {filteredFaqs.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-white/50">
            <HelpCircle className="w-12 h-12 mb-3 opacity-50" />
            <p className="text-lg font-medium">No FAQs yet</p>
            <p className="text-sm">Use categories to control whether an answer is used in direct messages, group chats, or both.</p>
          </div>
        ) : (
          filteredFaqs.map((faq, index) => (
            <FAQItem
              key={index}
              faq={faq}
              index={index}
              isEditing={editingIndex === index}
              isExpanded={expandedIndex === index}
              onEdit={setEditingIndex}
              onSave={handleSaveFAQ}
              onCancel={() => setEditingIndex(null)}
              onDelete={handleDeleteFAQ}
              onToggleExpand={(i) => setExpandedIndex(expandedIndex === i ? null : i)}
              onRemoveVariation={handleRemoveVariation}
              onRegenerateVariations={handleRegenerateVariations}
              isUpdatingVariations={variationUpdateIndex === index}
            />
          ))
        )}
      </div>

      {/* Stats Footer */}
      <div className="mt-4 pt-4 border-t border-white/10">
        <div className="flex items-center justify-between text-sm text-white/50">
          <span>{faqs.length} FAQ{faqs.length !== 1 ? 's' : ''} total</span>
          <span className="flex items-center gap-1">
            <Sparkles className="w-4 h-4" />
            Bot will learn from these answers
          </span>
        </div>
      </div>

      <FAQVariationModal
        isOpen={variationFlow.isOpen}
        mode={variationFlow.mode}
        draft={variationFlow.draft}
        generatedVariations={variationFlow.generatedVariations}
        selectedVariationKeys={variationFlow.selectedVariationKeys}
        onToggleVariation={handleToggleVariation}
        onUpdateVariation={handleUpdateVariation}
        onSave={handleSaveGeneratedFAQ}
        onSaveWithoutVariations={handleSaveWithoutVariations}
        onRegenerate={() => generateVariationsForDraft(
          variationFlow.draft,
          variationFlow.mode,
          variationFlow.faqIndex
        )}
        onCancel={closeVariationFlow}
        isGenerating={variationFlow.isGenerating}
        isSaving={variationFlow.isSaving}
        error={variationFlow.error}
      />
    </div>
  );
};

// ==================== SCRIPTS TAB ====================

// Script Step Editor Component
const ScriptStepEditor = ({ step, index, onChange, onRemove, canRemove }) => {
  return (
    <div className="p-4 bg-[#0a0f1c] rounded-xl border border-white/10 relative group">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-white/5 rounded cursor-move text-white/30">
            <GripVertical className="w-4 h-4" />
          </div>
          <span className="text-sm font-medium text-white">Bước {index + 1}</span>
        </div>
        {canRemove && (
          <button
            onClick={() => onRemove(index)}
            className="p-1.5 text-white/40 hover:text-red-400 hover:bg-red-500/10 rounded-lg opacity-0 group-hover:opacity-100 transition-all"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>

      <div className="space-y-3">
        <div>
          <label className="block text-xs text-white/50 mb-1">Tên bước</label>
          <input
            type="text"
            value={step.name || ""}
            onChange={(e) => onChange(index, { ...step, name: e.target.value })}
            placeholder="VD: Xác nhận sản phẩm"
            className="w-full px-3 py-2 text-sm bg-[#050810] border border-white/10 rounded-lg text-white placeholder-white/30 focus:outline-none focus:border-[#ff4d4d]"
          />
        </div>

        <div>
          <label className="block text-xs text-white/50 mb-1">Tin nhắn</label>
          <textarea
            value={step.message || ""}
            onChange={(e) => onChange(index, { ...step, message: e.target.value })}
            placeholder="Tin nhắn AI sẽ gửi..."
            rows={3}
            className="w-full px-3 py-2 text-sm bg-[#050810] border border-white/10 rounded-lg text-white placeholder-white/30 focus:outline-none focus:border-[#ff4d4d] resize-none"
          />
          <p className="mt-1 text-xs text-white/30">
            Dùng {"{field_name}"} để chèn giá trị đã thu thập
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-white/50 mb-1">Thu thập</label>
            <select
              value={step.collect_field || ""}
              onChange={(e) => onChange(index, { ...step, collect_field: e.target.value || null })}
              className="w-full px-3 py-2 text-sm bg-[#050810] border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#ff4d4d]"
            >
              {COLLECT_FIELDS.map((field) => (
                <option key={field.value} value={field.value} className="bg-[#0d1424]">
                  {field.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-white/50 mb-1">Validation</label>
            <select
              value={step.validation || ""}
              onChange={(e) => onChange(index, { ...step, validation: e.target.value || null })}
              className="w-full px-3 py-2 text-sm bg-[#050810] border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#ff4d4d]"
            >
              {VALIDATION_TYPES.map((type) => (
                <option key={type.value} value={type.value} className="bg-[#0d1424]">
                  {type.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};

// Template Picker Modal
const TemplatePicker = ({ isOpen, onSelect, onCancel }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="w-full max-w-4xl max-h-[85vh] overflow-hidden rounded-2xl border border-white/10 bg-[#050810] shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div>
            <h3 className="text-lg font-semibold text-white">Chọn Template</h3>
            <p className="text-sm text-white/50">Bắt đầu nhanh với một mẫu có sẵn hoặc tạo từ đầu</p>
          </div>
          <button onClick={onCancel} className="text-white/50 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Templates Grid */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Start from scratch option */}
          <button
            onClick={() => onSelect(null)}
            className="w-full mb-4 p-4 bg-[#0a0f1c] rounded-xl border-2 border-dashed border-white/20 hover:border-[#ff4d4d]/50 transition-colors text-left group"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center text-2xl group-hover:bg-[#ff4d4d]/10">
                ✨
              </div>
              <div>
                <h4 className="font-medium text-white group-hover:text-[#ff4d4d]">Tạo từ đầu</h4>
                <p className="text-sm text-white/50">Bắt đầu với một script trống</p>
              </div>
            </div>
          </button>

          {/* Templates */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {SCRIPT_TEMPLATES.map((template) => (
              <button
                key={template.id}
                onClick={() => onSelect(template)}
                className="p-4 bg-[#0a0f1c] rounded-xl border border-white/10 hover:border-[#ff4d4d]/50 transition-colors text-left group"
              >
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center text-2xl flex-shrink-0 group-hover:bg-[#ff4d4d]/10">
                    {template.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-white group-hover:text-[#ff4d4d] truncate">
                      {template.name}
                    </h4>
                    <p className="text-xs text-white/50 mt-0.5">{template.description}</p>
                    <div className="mt-2 flex items-center gap-2 text-xs text-white/40">
                      <span className="px-1.5 py-0.5 bg-white/5 rounded">
                        {SCRIPT_GOALS.find(g => g.value === template.goal)?.label || template.goal}
                      </span>
                      <span>{template.steps.length} bước</span>
                      <span>{template.trigger_phrases.length} triggers</span>
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Script Editor Modal
const ScriptEditorModal = ({ isOpen, script, onSave, onCancel, isSaving }) => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    goal: "order",
    trigger_phrases: [],
    steps: [{ step_id: 1, name: "", message: "", collect_field: null, validation: null }],
    success_message: "",
    cancel_message: "",
    is_active: true,
  });
  const [triggerInput, setTriggerInput] = useState("");

  useEffect(() => {
    if (script) {
      setFormData({
        name: script.name || "",
        description: script.description || "",
        goal: script.goal || "order",
        trigger_phrases: script.trigger_phrases || [],
        steps: script.steps?.length > 0 ? script.steps : [{ step_id: 1, name: "", message: "", collect_field: null, validation: null }],
        success_message: script.success_message || "",
        cancel_message: script.cancel_message || "",
        is_active: script.is_active !== false,
      });
      setTriggerInput((script.trigger_phrases || []).join("\n"));
    } else {
      setFormData({
        name: "",
        description: "",
        goal: "order",
        trigger_phrases: [],
        steps: [{ step_id: 1, name: "", message: "", collect_field: null, validation: null }],
        success_message: "",
        cancel_message: "",
        is_active: true,
      });
      setTriggerInput("");
    }
  }, [script, isOpen]);

  const handleTriggerChange = (value) => {
    setTriggerInput(value);
    const phrases = value.split("\n").map((p) => p.trim()).filter(Boolean);
    setFormData((prev) => ({ ...prev, trigger_phrases: phrases }));
  };

  const handleStepChange = (index, updatedStep) => {
    const newSteps = [...formData.steps];
    newSteps[index] = { ...updatedStep, step_id: index + 1 };
    setFormData((prev) => ({ ...prev, steps: newSteps }));
  };

  const handleAddStep = () => {
    const newStep = {
      step_id: formData.steps.length + 1,
      name: "",
      message: "",
      collect_field: null,
      validation: null,
    };
    setFormData((prev) => ({ ...prev, steps: [...prev.steps, newStep] }));
  };

  const handleRemoveStep = (index) => {
    if (formData.steps.length <= 1) return;
    const newSteps = formData.steps.filter((_, i) => i !== index).map((s, i) => ({ ...s, step_id: i + 1 }));
    setFormData((prev) => ({ ...prev, steps: newSteps }));
  };

  const handleSubmit = () => {
    if (!formData.name.trim() || formData.trigger_phrases.length === 0 || formData.steps.length === 0) {
      return;
    }
    onSave(formData);
  };

  const isValid = formData.name.trim() && formData.trigger_phrases.length > 0 && formData.steps.length > 0;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-2xl border border-white/10 bg-[#050810] shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <h3 className="text-lg font-semibold text-white">
            {script?.id ? "Chỉnh sửa Script" : "Tạo Target Script"}
          </h3>
          <button
            onClick={onCancel}
            disabled={isSaving}
            className="text-white/50 hover:text-white disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-white/60 mb-1">Tên Script *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="VD: Chốt Đơn"
                className="w-full px-4 py-2.5 text-sm bg-[#0a0f1c] border border-white/10 rounded-lg text-white placeholder-white/30 focus:outline-none focus:border-[#ff4d4d]"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-white/60 mb-1">Mục tiêu *</label>
              <select
                value={formData.goal}
                onChange={(e) => setFormData((prev) => ({ ...prev, goal: e.target.value }))}
                className="w-full px-4 py-2.5 text-sm bg-[#0a0f1c] border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#ff4d4d]"
              >
                {SCRIPT_GOALS.map((goal) => (
                  <option key={goal.value} value={goal.value} className="bg-[#0d1424]">
                    {goal.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-white/60 mb-1">Mô tả</label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
              placeholder="Mô tả ngắn về script này"
              className="w-full px-4 py-2.5 text-sm bg-[#0a0f1c] border border-white/10 rounded-lg text-white placeholder-white/30 focus:outline-none focus:border-[#ff4d4d]"
            />
          </div>

          {/* Trigger Phrases */}
          <div>
            <label className="block text-xs font-medium text-white/60 mb-1">
              Trigger Phrases * <span className="text-white/40">(mỗi dòng 1 phrase)</span>
            </label>
            <textarea
              value={triggerInput}
              onChange={(e) => handleTriggerChange(e.target.value)}
              placeholder={"giá bao nhiêu\nbao nhiêu tiền\nmuốn mua\nđặt hàng"}
              rows={4}
              className="w-full px-4 py-2.5 text-sm bg-[#0a0f1c] border border-white/10 rounded-lg text-white placeholder-white/30 focus:outline-none focus:border-[#ff4d4d] resize-none font-mono"
            />
            <p className="mt-1 text-xs text-white/40">
              Khi khách hàng nhắn những cụm từ này, AI sẽ bắt đầu script
            </p>
          </div>

          {/* Steps */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="text-xs font-medium text-white/60">Các bước</label>
              <button
                onClick={handleAddStep}
                className="px-3 py-1.5 text-xs bg-white/5 hover:bg-white/10 text-white rounded-lg flex items-center gap-1.5 transition-colors"
              >
                <Plus className="w-3.5 h-3.5" />
                Thêm bước
              </button>
            </div>
            <div className="space-y-3">
              {formData.steps.map((step, index) => (
                <ScriptStepEditor
                  key={index}
                  step={step}
                  index={index}
                  onChange={handleStepChange}
                  onRemove={handleRemoveStep}
                  canRemove={formData.steps.length > 1}
                />
              ))}
            </div>
          </div>

          {/* Success/Cancel Messages */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-white/60 mb-1">Tin nhắn thành công</label>
              <textarea
                value={formData.success_message}
                onChange={(e) => setFormData((prev) => ({ ...prev, success_message: e.target.value }))}
                placeholder="Đặt hàng thành công! 🎉"
                rows={2}
                className="w-full px-4 py-2.5 text-sm bg-[#0a0f1c] border border-white/10 rounded-lg text-white placeholder-white/30 focus:outline-none focus:border-[#ff4d4d] resize-none"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-white/60 mb-1">Tin nhắn hủy</label>
              <textarea
                value={formData.cancel_message}
                onChange={(e) => setFormData((prev) => ({ ...prev, cancel_message: e.target.value }))}
                placeholder="Dạ không sao ạ! Khi nào cần cứ nhắn em nhé 😊"
                rows={2}
                className="w-full px-4 py-2.5 text-sm bg-[#0a0f1c] border border-white/10 rounded-lg text-white placeholder-white/30 focus:outline-none focus:border-[#ff4d4d] resize-none"
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-white/10">
          <button
            onClick={onCancel}
            disabled={isSaving}
            className="px-4 py-2 text-sm text-white/60 hover:bg-white/5 rounded-lg disabled:opacity-50 transition-colors"
          >
            Hủy
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSaving || !isValid}
            className="px-4 py-2 text-sm bg-[#ff4d4d] hover:bg-[#ff5c5c] text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
          >
            {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {script?.id ? "Cập nhật" : "Tạo Script"}
          </button>
        </div>
      </div>
    </div>
  );
};

// Scripts Tab Content
const ScriptsTab = () => {
  const [scripts, setScripts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accountId, setAccountId] = useState(null);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [editingScript, setEditingScript] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [togglingScript, setTogglingScript] = useState(null);

  // Load scripts
  const loadScripts = useCallback(async (acctId) => {
    try {
      setIsLoading(true);
      setError(null);
      const result = await fetchTargetScripts({ accountId: acctId });
      setScripts(result.scripts || []);
    } catch (err) {
      console.error("[ScriptsTab] Failed to load scripts:", err);
      setError("Failed to load scripts. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      try {
        const accountsResult = await fetchClaw9Accounts();
        const accounts = accountsResult.accounts || [];
        if (accounts.length > 0) {
          const acctId = accounts[0].account_id;
          setAccountId(acctId);
          await loadScripts(acctId);
        } else {
          setError("No accounts found. Please configure your account first.");
          setIsLoading(false);
        }
      } catch (err) {
        setError("Failed to load account.");
        setIsLoading(false);
      }
    };
    init();
  }, [loadScripts]);

  const handleCreateScript = () => {
    setShowTemplatePicker(true);
  };

  const handleSelectTemplate = (template) => {
    setShowTemplatePicker(false);
    if (template) {
      // Use template as starting point (without id so it creates new)
      setEditingScript({
        name: template.name,
        description: template.description,
        goal: template.goal,
        trigger_phrases: [...template.trigger_phrases],
        steps: template.steps.map((s, i) => ({ ...s, step_id: i + 1 })),
        success_message: template.success_message,
        cancel_message: template.cancel_message,
        is_active: true,
      });
    } else {
      setEditingScript(null);
    }
    setShowEditor(true);
  };

  const handleEditScript = (script) => {
    setEditingScript(script);
    setShowEditor(true);
  };

  const handleSaveScript = async (formData) => {
    if (!accountId) return;
    setIsSaving(true);

    try {
      if (editingScript?.id) {
        // Update existing script
        await updateTargetScript(editingScript.id, formData, accountId);
      } else {
        // Create new script (either from scratch or from template)
        await createTargetScript(formData, accountId);
      }
      setShowEditor(false);
      setEditingScript(null);
      await loadScripts(accountId);
    } catch (err) {
      console.error("[ScriptsTab] Failed to save script:", err);
      setError(err.message || "Failed to save script.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteScript = async (script) => {
    if (!window.confirm(`Xóa script "${script.name}"?`)) return;
    if (!accountId) return;

    try {
      await deleteTargetScript(script.id, accountId);
      await loadScripts(accountId);
    } catch (err) {
      console.error("[ScriptsTab] Failed to delete script:", err);
      setError(err.message || "Failed to delete script.");
    }
  };

  const handleToggleScript = async (script) => {
    if (!accountId) return;
    setTogglingScript(script.id);

    try {
      await toggleTargetScript(script.id, !script.is_active, accountId);
      setScripts((prev) =>
        prev.map((s) => (s.id === script.id ? { ...s, is_active: !s.is_active } : s))
      );
    } catch (err) {
      console.error("[ScriptsTab] Failed to toggle script:", err);
      setError(err.message || "Failed to toggle script.");
    } finally {
      setTogglingScript(null);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="h-full flex flex-col items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#ff4d4d]" />
        <p className="mt-4 text-white/50">Loading scripts...</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Error banner */}
      {error && (
        <div className="mb-4 p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 text-sm flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-300">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-white">Target Scripts</h2>
          <p className="text-sm text-white/50">
            Định nghĩa các kịch bản bán hàng để AI dẫn dắt cuộc hội thoại
          </p>
        </div>
        <button
          onClick={handleCreateScript}
          className="px-4 py-2.5 bg-[#ff4d4d] hover:bg-[#ff5c5c] text-white rounded-lg font-medium flex items-center gap-2 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Tạo mới
        </button>
      </div>

      {/* Scripts List */}
      <div className="flex-1 min-h-0 overflow-y-auto space-y-3 pr-1">
        {scripts.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-white/50">
            <Target className="w-12 h-12 mb-3 opacity-50" />
            <p className="text-lg font-medium">Chưa có script nào</p>
            <p className="text-sm">Tạo script để AI hướng dẫn khách hàng qua quy trình bán hàng</p>
            <button
              onClick={handleCreateScript}
              className="mt-4 px-4 py-2 bg-[#ff4d4d] hover:bg-[#ff5c5c] text-white rounded-lg text-sm flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Tạo Script đầu tiên
            </button>
          </div>
        ) : (
          scripts.map((script) => (
            <div
              key={script.id}
              className={`p-4 bg-[#0a0f1c] rounded-xl border transition-all ${
                script.is_active ? "border-emerald-500/30" : "border-white/10"
              }`}
            >
              <div className="flex items-start justify-between gap-4">
                {/* Script Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-lg">{GOAL_ICONS[script.goal] || "📋"}</span>
                    <h3 className="font-medium text-white">{script.name}</h3>
                    <span
                      className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                        script.is_active
                          ? "bg-emerald-500/20 text-emerald-400"
                          : "bg-white/10 text-white/50"
                      }`}
                    >
                      {script.is_active ? "Active" : "Inactive"}
                    </span>
                  </div>
                  {script.description && (
                    <p className="text-sm text-white/50 mt-1">{script.description}</p>
                  )}
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {(script.trigger_phrases || []).slice(0, 4).map((phrase, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-0.5 text-xs bg-white/5 text-white/60 rounded"
                      >
                        "{phrase}"
                      </span>
                    ))}
                    {(script.trigger_phrases || []).length > 4 && (
                      <span className="px-2 py-0.5 text-xs bg-white/5 text-white/40 rounded">
                        +{script.trigger_phrases.length - 4} more
                      </span>
                    )}
                  </div>
                  <div className="mt-2 text-xs text-white/40">
                    Goal: {SCRIPT_GOALS.find((g) => g.value === script.goal)?.label || script.goal} |{" "}
                    Steps: {script.steps?.length || 0}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => handleToggleScript(script)}
                    disabled={togglingScript === script.id}
                    className={`p-2 rounded-lg transition-colors ${
                      script.is_active
                        ? "text-emerald-400 hover:bg-emerald-500/10"
                        : "text-white/40 hover:bg-white/5"
                    }`}
                    title={script.is_active ? "Tắt" : "Bật"}
                  >
                    {togglingScript === script.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : script.is_active ? (
                      <Pause className="w-4 h-4" />
                    ) : (
                      <Play className="w-4 h-4" />
                    )}
                  </button>
                  <button
                    onClick={() => handleEditScript(script)}
                    className="p-2 text-white/40 hover:text-[#ff4d4d] hover:bg-white/5 rounded-lg transition-colors"
                    title="Sửa"
                  >
                    <Edit3 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteScript(script)}
                    className="p-2 text-white/40 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                    title="Xóa"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Stats Footer */}
      <div className="mt-4 pt-4 border-t border-white/10">
        <div className="flex items-center justify-between text-sm text-white/50">
          <span>
            {scripts.filter((s) => s.is_active).length} of {scripts.length} script
            {scripts.length !== 1 ? "s" : ""} active
          </span>
          <span className="flex items-center gap-1">
            <Target className="w-4 h-4" />
            AI sẽ sử dụng scripts khi khách hàng nhắn trigger phrases
          </span>
        </div>
      </div>

      {/* Template Picker Modal */}
      <TemplatePicker
        isOpen={showTemplatePicker}
        onSelect={handleSelectTemplate}
        onCancel={() => setShowTemplatePicker(false)}
      />

      {/* Editor Modal */}
      <ScriptEditorModal
        isOpen={showEditor}
        script={editingScript}
        onSave={handleSaveScript}
        onCancel={() => {
          setShowEditor(false);
          setEditingScript(null);
        }}
        isSaving={isSaving}
      />
    </div>
  );
};

// Main Component
const Claw9Agents = ({ userEmail }) => {
  const isAdmin = userEmail === 'andrew@claw9.ai';
  const [activeTab, setActiveTab] = useState(isAdmin ? "agents" : "faqs");

  // Filter tabs - "agents" tab only visible to andrew@claw9.ai
  const visibleTabs = AGENT_TABS.filter(tab => {
    if (tab.id === 'agents' && !isAdmin) return false;
    return true;
  });

  return (
    <div className="flex flex-1 flex-col min-h-0 h-full bg-[#050810] border border-white/10 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="relative overflow-hidden px-6 py-4 border-b border-white/10">
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(120deg, rgba(255, 77, 77, 0.9), rgba(10, 15, 28, 0.95))'
          }}
        />
        <div className="relative flex items-center justify-between">
          <div className="text-white">
            <h1 className="text-xl font-semibold flex items-center gap-2">
              <Bot className="w-5 h-5" /> AI Agents
            </h1>
            <p className="text-xs text-white/70 mt-0.5">
              Configure your AI-powered customer service bots
            </p>
          </div>
          <div className="hidden sm:flex gap-2 text-xs">
            <span className="px-3 py-1.5 rounded-full bg-white/15 text-white/80 font-medium">
              Knowledge Base
            </span>
            <span className="px-3 py-1.5 rounded-full bg-white/15 text-white/80 font-medium">
              Auto-Reply
            </span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="px-6 pt-4 border-b border-white/10 bg-[#0a0f1c]/50">
        <div className="flex gap-1">
          {visibleTabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => !tab.disabled && setActiveTab(tab.id)}
                disabled={tab.disabled}
                className={`px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors flex items-center gap-2 ${
                  activeTab === tab.id
                    ? "bg-[#0d1424] text-[#ff4d4d] border-t border-l border-r border-white/10 -mb-px"
                    : tab.disabled
                    ? "text-white/30 cursor-not-allowed"
                    : "text-white/60 hover:text-white hover:bg-white/5"
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
                {tab.disabled && (
                  <span className="text-[10px] px-1.5 py-0.5 bg-white/10 text-white/40 rounded">
                    Soon
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      <div className="flex-1 min-h-0 overflow-y-auto p-6 bg-[#050810]">
        {activeTab === "agents" && isAdmin && <AgentsTab />}
        {activeTab === "faqs" && <FAQsTab />}
        {activeTab === "scripts" && <ScriptsTab />}
        {activeTab === "knowledge" && (
          <Claw9KnowledgeBase />
        )}
        {activeTab === "prompts" && (
          <div className="flex flex-col items-center justify-center h-full text-white/50">
            <MessageSquare className="w-12 h-12 mb-3 opacity-50" />
            <p className="text-lg font-medium">Custom Prompts</p>
            <p className="text-sm">Coming soon...</p>
          </div>
        )}
        {activeTab === "settings" && (
          <div className="flex flex-col items-center justify-center h-full text-white/50">
            <Settings className="w-12 h-12 mb-3 opacity-50" />
            <p className="text-lg font-medium">Bot Settings</p>
            <p className="text-sm">Coming soon...</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Claw9Agents;
